import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import './AdminDashboard.css';
import {
    HiOutlineChevronLeft, HiOutlinePlus, HiOutlineSearch, HiOutlineAdjustments,
    HiOutlineOfficeBuilding, HiOutlineX, HiOutlinePencil, HiOutlineKey,
    HiOutlineLockClosed, HiOutlineTrash, HiOutlineUsers, HiOutlineShieldCheck,
    HiOutlineExclamationCircle, HiOutlineSpeakerphone, HiOutlineCheck,
    HiOutlineMicrophone, HiOutlinePhotograph
} from 'react-icons/hi';
import RoleManagement from './RoleManagement';
import PermissionEditor from './PermissionEditor';
import AnnouncementAdmin from './AnnouncementAdmin';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('users'); // 'users', 'roles', 'bugReports', 'announcement'
    const [users, setUsers] = useState([]);
    const [bugReports, setBugReports] = useState([]);
    const [bugReportsLoading, setBugReportsLoading] = useState(false);
    // Voice clips and screenshots are fetched lazily through the authenticated
    // stream routes (a plain <audio>/<img> src can't carry the JWT).
    // id -> objectURL | 'loading' | 'error'
    const [bugAudio, setBugAudio] = useState({});
    const [bugImage, setBugImage] = useState({});
    const [bugStatus, setBugStatus] = useState('open');       // 'open' | 'resolved'
    const [bugCounts, setBugCounts] = useState({ open: 0, resolved: 0 });
    const [bugFullReport, setBugFullReport] = useState({});   // id -> full console text
    const [bugBusy, setBugBusy] = useState({});               // id -> true while its status PATCH is in flight
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [companies, setCompanies] = useState([]);   // tenant companies
    const [branches, setBranches] = useState([]);     // branches of the selected company
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [showCreateDeptModal, setShowCreateDeptModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Row filters for the Users table (client-side, applied on top of the search results)
    const [filterRole, setFilterRole] = useState('');
    const [filterDepartment, setFilterDepartment] = useState('');
    const [filterBranch, setFilterBranch] = useState('');
    const [filterCreatedFrom, setFilterCreatedFrom] = useState('');
    const [filterCreatedTo, setFilterCreatedTo] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false); // phones only — always shown on desktop

    // Form states
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        fullName: '',
        role: '',
        department: '',
        company: '',
        branch: ''
    });
    const [newPassword, setNewPassword] = useState('');
    const [newDepartment, setNewDepartment] = useState({ name: '', description: '' });
    // Department heads. Heads decide who can see a department's tasks in Task
    // Management (backend: utils/taskScope.js); nobody has that visibility until
    // a head is assigned here.
    const [showManageDeptModal, setShowManageDeptModal] = useState(false);
    const [deptHeadsDraft, setDeptHeadsDraft] = useState({});
    const [allUsers, setAllUsers] = useState([]);
    const [savingDeptId, setSavingDeptId] = useState(null);
    const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
    const [showCreateBranchModal, setShowCreateBranchModal] = useState(false);
    const [newCompany, setNewCompany] = useState({ name: '', code: '' });
    const [newBranch, setNewBranch] = useState({ name: '', code: '', branchType: 'sales' });

    // Fetch all users (optionally filtered by a search term).
    // `silent` skips the full-page spinner so the search input stays mounted
    // (otherwise the input unmounts mid-type and the cursor drops).
    const fetchUsers = async (search = '', { silent = false } = {}) => {
        try {
            if (silent) setSearching(true); else setLoading(true);
            const response = await axiosInstance.get('/api/users', {
                params: search ? { search } : {}
            });
            setUsers(response.data.users);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch users');
        } finally {
            if (silent) setSearching(false); else setLoading(false);
        }
    };

    // Fetch all departments
    const fetchDepartments = async () => {
        try {
            const response = await axiosInstance.get('/api/departments');
            setDepartments(response.data.data);
        } catch (err) {
            console.error('Failed to fetch departments:', err);
        }
    };

    // Fetch tenant companies (for the company dropdown)
    const fetchCompanies = async () => {
        try {
            const response = await axiosInstance.get('/api/tenants');
            setCompanies(response.data.tenants || []);
        } catch (err) {
            console.error('Failed to fetch companies:', err);
        }
    };

    // Fetch branches belonging to a company (for the branch dropdown)
    const fetchBranches = async (companyId) => {
        if (!companyId) { setBranches([]); return; }
        try {
            const response = await axiosInstance.get(`/api/tenants?company=${companyId}`);
            setBranches(response.data.tenants || []);
        } catch (err) {
            console.error('Failed to fetch branches:', err);
            setBranches([]);
        }
    };

    // Fetch all roles
    const fetchRoles = async () => {
        try {
            const response = await axiosInstance.get('/api/roles');
            const list = response.data?.roles ?? response.data?.data ?? [];
            setRoles(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Failed to fetch roles:', err);
            setRoles([]);
        }
    };

    const fetchBugReports = async (status = bugStatus) => {
        try {
            setBugReportsLoading(true);
            const res = await axiosInstance.get(`/api/bug-reports?limit=100&status=${status}`);
            setBugReports(res.data.data || []);
            if (res.data.counts) setBugCounts(res.data.counts);
        } catch (err) {
            console.error('Failed to fetch bug reports:', err);
        } finally {
            setBugReportsLoading(false);
        }
    };

    const changeBugStatus = async (id, next) => {
        setBugBusy((prev) => ({ ...prev, [id]: true }));
        try {
            await axiosInstance.patch(`/api/bug-reports/${id}`, { status: next });
            // The row no longer belongs in the current view — drop it and adjust
            // both counters without a full refetch.
            setBugReports((prev) => prev.filter((r) => r._id !== id));
            setBugCounts((prev) => ({
                open: prev.open + (next === 'open' ? 1 : -1),
                resolved: prev.resolved + (next === 'resolved' ? 1 : -1)
            }));
        } catch (err) {
            console.error('Failed to update bug report:', err);
            alert('Could not update the report. Please try again.');
        } finally {
            setBugBusy((prev) => ({ ...prev, [id]: false }));
        }
    };

    const loadFullReport = async (id) => {
        setBugFullReport((prev) => ({ ...prev, [id]: 'loading' }));
        try {
            const res = await axiosInstance.get(`/api/bug-reports/${id}`);
            setBugFullReport((prev) => ({ ...prev, [id]: res.data.data?.report || '' }));
        } catch (err) {
            console.error('Failed to load full report:', err);
            setBugFullReport((prev) => ({ ...prev, [id]: 'error' }));
        }
    };

    const loadBugAudio = async (id) => {
        setBugAudio((prev) => ({ ...prev, [id]: 'loading' }));
        try {
            const res = await axiosInstance.get(`/api/bug-reports/${id}/audio`, { responseType: 'blob' });
            setBugAudio((prev) => ({ ...prev, [id]: URL.createObjectURL(res.data) }));
        } catch (err) {
            console.error('Failed to load bug audio:', err);
            setBugAudio((prev) => ({ ...prev, [id]: 'error' }));
        }
    };

    const loadBugImage = async (id) => {
        setBugImage((prev) => ({ ...prev, [id]: 'loading' }));
        try {
            const res = await axiosInstance.get(`/api/bug-reports/${id}/screenshot`, { responseType: 'blob' });
            setBugImage((prev) => ({ ...prev, [id]: URL.createObjectURL(res.data) }));
        } catch (err) {
            console.error('Failed to load bug screenshot:', err);
            setBugImage((prev) => ({ ...prev, [id]: 'error' }));
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchRoles();
        fetchCompanies();
    }, []);

    // Initial user load — the only time the full-page spinner shows.
    useEffect(() => {
        fetchUsers('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced user search. Runs silently (no full-page spinner) so the search
    // input never unmounts mid-type. Skips the first render (handled above).
    const didMountSearch = useRef(false);
    useEffect(() => {
        if (!didMountSearch.current) { didMountSearch.current = true; return; }
        const timer = setTimeout(() => fetchUsers(searchTerm, { silent: true }), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Whenever the selected company changes, reload its branches.
    useEffect(() => {
        fetchBranches(formData.company);
    }, [formData.company]);

    useEffect(() => {
        if (activeTab === 'bugReports') fetchBugReports(bugStatus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, bugStatus]);

    // Create user
    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post('/api/users', formData);
            setShowCreateModal(false);
            setFormData({ username: '', password: '', fullName: '', role: '', department: '', company: '', branch: '' });
            fetchUsers();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to create user');
        }
    };

    // Update user
    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.put(`/api/users/${selectedUser._id}`, {
                username: formData.username,
                fullName: formData.fullName,
                role: formData.role,
                department: formData.department,
                company: formData.company,
                branch: formData.branch
            });
            setShowEditModal(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update user');
        }
    };

    // Create department
    const handleCreateDepartment = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post('/api/departments', newDepartment);
            setShowCreateDeptModal(false);
            setNewDepartment({ name: '', description: '' });
            fetchDepartments();
            alert('Department created successfully');
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to create department');
        }
    };

    // Open the department manager: pull an unfiltered user list for the head
    // picker (the main `users` list is narrowed by the search box) and seed the
    // draft from what is currently stored.
    const openManageDepartments = async () => {
        try {
            const [deptRes, userRes] = await Promise.all([
                axiosInstance.get('/api/departments', { params: { includeInactive: 'true' } }),
                axiosInstance.get('/api/users')
            ]);
            const depts = deptRes.data.data || [];
            setDepartments(depts);
            setAllUsers(userRes.data.users || []);
            const draft = {};
            depts.forEach(d => {
                draft[d._id] = (d.heads || []).map(h => String(h._id || h));
            });
            setDeptHeadsDraft(draft);
            setShowManageDeptModal(true);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to load departments');
        }
    };

    const handleSaveDeptHeads = async (deptId) => {
        setSavingDeptId(deptId);
        try {
            await axiosInstance.put(`/api/departments/${deptId}`, {
                heads: deptHeadsDraft[deptId] || []
            });
            await fetchDepartments();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update department heads');
        } finally {
            setSavingDeptId(null);
        }
    };

    // Create a new company (tenant), then auto-select it
    const handleCreateCompany = async (e) => {
        e.preventDefault();
        try {
            const res = await axiosInstance.post('/api/tenants', {
                type: 'company',
                name: newCompany.name,
                code: newCompany.code
            });
            setShowCreateCompanyModal(false);
            setNewCompany({ name: '', code: '' });
            await fetchCompanies();
            setFormData(prev => ({ ...prev, company: res.data.tenant._id, branch: '' }));
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to create company');
        }
    };

    // Create a new branch under the currently selected company, then auto-select it
    const handleCreateBranch = async (e) => {
        e.preventDefault();
        if (!formData.company) {
            alert('Select a company first');
            return;
        }
        try {
            const res = await axiosInstance.post('/api/tenants', {
                type: 'branch',
                name: newBranch.name,
                code: newBranch.code,
                branchType: newBranch.branchType,
                parent: formData.company
            });
            setShowCreateBranchModal(false);
            setNewBranch({ name: '', code: '', branchType: 'sales' });
            await fetchBranches(formData.company);
            setFormData(prev => ({ ...prev, branch: res.data.tenant._id }));
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to create branch');
        }
    };

    // Reset password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post(`/api/users/${selectedUser._id}/reset-password`, {
                newPassword
            });
            setShowPasswordModal(false);
            setSelectedUser(null);
            setNewPassword('');
            alert('Password reset successfully');
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to reset password');
        }
    };

    // Delete user
    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        
        try {
            await axiosInstance.delete(`/api/users/${userId}`);
            fetchUsers();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to delete user');
        }
    };

    // Open edit modal
    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            username: user.username,
            fullName: user.fullName,
            role: user.role || '',  // roleId string
            department: user.department?._id || '',
            company: user.company?._id || '',
            branch: user.branch?._id || '',
            password: ''
        });
        setShowEditModal(true);
    };

    // Open password modal
    const openPasswordModal = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setShowPasswordModal(true);
    };

    // Open permission modal
    const openPermissionModal = (user) => {
        setSelectedUser(user);
        setShowPermissionModal(true);
    };

    // Get role badge color
    const getRoleBadgeClass = (roleData) => {
        if (!roleData) return 'badge-default';
        const roleId = typeof roleData === 'string' ? roleData : (roleData.roleId || '');
        const colors = {
            admin: 'badge-admin',
            manager: 'badge-manager',
            receptionist: 'badge-receptionist',
            accounts: 'badge-accounts',
            visitor: 'badge-visitor',
            employee: 'badge-employee'
        };
        return colors[roleId] || 'badge-default';
    };

    // Unique branches present in the current user list (branches state is scoped to
    // the create/edit form's selected company, so it can't be reused as filter options)
    const branchOptions = Array.from(
        users.reduce((map, u) => {
            if (u.branch?._id) {
                const label = u.company?.name ? `${u.company.name} — ${u.branch.name}` : u.branch.name;
                map.set(u.branch._id, label);
            }
            return map;
        }, new Map())
    ).map(([id, label]) => ({ id, label }));

    const filteredUsers = users.filter(user => {
        if (filterRole && user.role !== filterRole) return false;
        if (filterDepartment && user.department?._id !== filterDepartment) return false;
        if (filterBranch && user.branch?._id !== filterBranch) return false;
        if (filterCreatedFrom || filterCreatedTo) {
            const created = new Date(user.createdAt);
            if (filterCreatedFrom && created < new Date(filterCreatedFrom + 'T00:00:00')) return false;
            if (filterCreatedTo && created > new Date(filterCreatedTo + 'T23:59:59')) return false;
        }
        return true;
    });

    const hasActiveFilters = filterRole || filterDepartment || filterBranch || filterCreatedFrom || filterCreatedTo;

    const clearFilters = () => {
        setFilterRole('');
        setFilterDepartment('');
        setFilterBranch('');
        setFilterCreatedFrom('');
        setFilterCreatedTo('');
    };

    const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2)
        .map((w) => w[0]).join('').toUpperCase() || '?';

    if (loading) {
        return <div className="um-page"><div className="loading">Loading...</div></div>;
    }

    return (
        <div className="um-page">
            <header className="um-header">
                <button className="um-back" onClick={() => navigate('/purpose-selection')} title="Back to home">
                    <HiOutlineChevronLeft />
                </button>
                <div className="um-title">
                    <h1>User Management</h1>
                    <p>Users, roles, permissions and system notices</p>
                </div>
                {activeTab === 'users' && (
                    <button className="um-btn um-btn-primary" onClick={() => setShowCreateModal(true)}>
                        <HiOutlinePlus /> <span>Add User</span>
                    </button>
                )}
            </header>

            {/* Own `um-` classes: AdminDashboard.css is global, and `.tab-button`
                is shared with Cashbook, Cheque Register and Document Log Book. */}
            <nav className="um-tabs">
                {[
                    { id: 'users', label: 'Users', icon: <HiOutlineUsers />, count: users.length },
                    { id: 'roles', label: 'Roles & Permissions', icon: <HiOutlineShieldCheck /> },
                    { id: 'bugReports', label: 'Bug Reports', icon: <HiOutlineExclamationCircle />, count: bugCounts.open || null },
                    ...(user?.role === 'system_admin'
                        ? [{ id: 'announcement', label: 'Announcement', icon: <HiOutlineSpeakerphone /> }]
                        : []),
                ].map((t) => (
                    <button
                        key={t.id}
                        className={`um-tab${activeTab === t.id ? ' active' : ''}`}
                        onClick={() => setActiveTab(t.id)}
                    >
                        {t.icon} {t.label}
                        {t.count != null && <span className="um-tab-count">{t.count}</span>}
                    </button>
                ))}
            </nav>

            <div className="um-body">
                {error && <div className="alert alert-error">{error}</div>}

                {activeTab === 'bugReports' ? (
                    <section className="um-bugs">
                        <div className="um-segment" role="group" aria-label="Bug report status">
                            {['open', 'resolved'].map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    className={bugStatus === s ? 'active' : ''}
                                    onClick={() => setBugStatus(s)}
                                >
                                    {s === 'open' ? 'Open' : 'Resolved'}
                                    <span className="um-tab-count">{s === 'open' ? bugCounts.open : bugCounts.resolved}</span>
                                </button>
                            ))}
                        </div>
                        {bugReportsLoading ? (
                            <div className="um-empty">Loading…</div>
                        ) : bugReports.length === 0 ? (
                            <div className="um-empty">{bugStatus === 'open' ? 'No open bug reports. 🎉' : 'No resolved bug reports.'}</div>
                        ) : (
                            <div className="um-bug-list">
                                {bugReports.map((r) => {
                                    const a = bugAudio[r._id];
                                    const img = bugImage[r._id];
                                    const full = bugFullReport[r._id];
                                    const consoleText = (full && full !== 'loading' && full !== 'error'
                                        ? full
                                        : (r.report || '')).replace(/<[^>]*>/g, '');
                                    const note = r.note ? r.note.replace(/<[^>]*>/g, '') : '';
                                    const url = (r.url || '').replace(/<[^>]*>/g, '');
                                    return (
                                        <article key={r._id} className={`um-bug${r.status === 'resolved' ? ' resolved' : ''}`}>
                                            <header className="um-bug-head">
                                                <span className="um-avatar">{initials(r.reportedByName || '?')}</span>
                                                <div className="um-bug-who">
                                                    <div className="um-name">{r.reportedByName || 'Anonymous'}</div>
                                                    <div className="um-sub">{new Date(r.createdAt).toLocaleString()}</div>
                                                </div>
                                                <div className="um-bug-status">
                                                    {r.status === 'resolved' ? (
                                                        <>
                                                            <span className="um-chip um-ok">
                                                                <HiOutlineCheck /> Resolved{r.resolvedByName ? ` by ${r.resolvedByName}` : ''}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="um-btn um-btn-sm"
                                                                disabled={bugBusy[r._id]}
                                                                onClick={() => changeBugStatus(r._id, 'open')}
                                                            >
                                                                {bugBusy[r._id] ? '…' : 'Reopen'}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="um-btn um-btn-sm um-btn-primary"
                                                            disabled={bugBusy[r._id]}
                                                            onClick={() => changeBugStatus(r._id, 'resolved')}
                                                        >
                                                            <HiOutlineCheck /> {bugBusy[r._id] ? '…' : 'Mark resolved'}
                                                        </button>
                                                    )}
                                                </div>
                                            </header>

                                            {url && <div className="um-bug-url" title={url}>{url}</div>}

                                            <div className="um-bug-body">
                                                <div className="um-bug-main">
                                                    {r.audio && (
                                                        a && a !== 'loading' && a !== 'error' ? (
                                                            <audio controls src={a} className="um-bug-audio" />
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="um-btn um-btn-sm"
                                                                disabled={a === 'loading'}
                                                                onClick={() => loadBugAudio(r._id)}
                                                            >
                                                                <HiOutlineMicrophone />
                                                                {a === 'loading' ? 'Loading…' : a === 'error' ? 'Retry' : 'Play recording'}
                                                                {r.audio.durationSeconds ? ` (${Math.round(r.audio.durationSeconds)}s)` : ''}
                                                            </button>
                                                        )
                                                    )}
                                                    {note && <p className="um-bug-note">{note}</p>}
                                                    {!note && !r.audio && <p className="um-none">No note or recording.</p>}
                                                </div>
                                                {r.screenshot && (
                                                    <div className="um-bug-shot">
                                                        {img && img !== 'loading' && img !== 'error' ? (
                                                            <a href={img} target="_blank" rel="noopener noreferrer">
                                                                <img src={img} alt="Bug screenshot" />
                                                            </a>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="um-btn um-btn-sm"
                                                                disabled={img === 'loading'}
                                                                onClick={() => loadBugImage(r._id)}
                                                            >
                                                                <HiOutlinePhotograph />
                                                                {img === 'loading' ? 'Loading…' : img === 'error' ? 'Retry' : 'View screenshot'}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {consoleText && (
                                                <details className="um-bug-console">
                                                    <summary>Console output</summary>
                                                    <pre>{consoleText}</pre>
                                                    {r.reportTruncated && !full && (
                                                        <button type="button" className="um-link" onClick={() => loadFullReport(r._id)}>
                                                            Show full output
                                                        </button>
                                                    )}
                                                    {full === 'loading' && <span className="um-sub"> loading…</span>}
                                                    {full === 'error' && <span className="um-error"> failed to load</span>}
                                                </details>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                ) : activeTab === 'roles' ? (
                    <RoleManagement />
                ) : activeTab === 'announcement' ? (
                    <AnnouncementAdmin />
                ) : (
                    <section className="um-users">
                        <div className="um-toolbar">
                            <label className="um-search">
                                <HiOutlineSearch />
                                <input
                                    type="text"
                                    placeholder="Search by ID, username or name…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searching && <span className="um-searching">Searching…</span>}
                            </label>
                            <button
                                type="button"
                                className={`um-btn um-filter-toggle${hasActiveFilters ? ' active' : ''}`}
                                onClick={() => setFiltersOpen((o) => !o)}
                                aria-expanded={filtersOpen}
                            >
                                <HiOutlineAdjustments /> Filters{hasActiveFilters ? ' •' : ''}
                            </button>
                            <button
                                type="button"
                                className="um-btn"
                                onClick={openManageDepartments}
                                title="Assign department heads — controls who can see a department's tasks"
                            >
                                <HiOutlineOfficeBuilding /> <span>Departments</span>
                            </button>
                        </div>

                        <div className={`um-filters${filtersOpen ? ' open' : ''}`}>
                            <label>Role
                                <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                                    <option value="">All roles</option>
                                    {roles.map(role => (
                                        <option key={role._id} value={role.roleId}>
                                            {role.displayName || role.roleName}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label>Department
                                <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}>
                                    <option value="">All departments</option>
                                    {departments.map(dept => (
                                        <option key={dept._id} value={dept._id}>{dept.name}</option>
                                    ))}
                                </select>
                            </label>
                            <label>Branch
                                <select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
                                    <option value="">All branches</option>
                                    {branchOptions.map(b => (
                                        <option key={b.id} value={b.id}>{b.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label>Created from
                                <input type="date" value={filterCreatedFrom} onChange={(e) => setFilterCreatedFrom(e.target.value)} />
                            </label>
                            <label>Created to
                                <input type="date" value={filterCreatedTo} onChange={(e) => setFilterCreatedTo(e.target.value)} />
                            </label>
                        </div>

                        <div className="um-summary">
                            <span>
                                Showing <strong>{filteredUsers.length}</strong>
                                {filteredUsers.length !== users.length && <> of {users.length}</>} users
                            </span>
                            {hasActiveFilters && (
                                <button type="button" className="um-link" onClick={clearFilters}>
                                    <HiOutlineX /> Clear filters
                                </button>
                            )}
                        </div>

                        {filteredUsers.length === 0 ? (
                            <div className="um-empty">No users match.</div>
                        ) : (
                            <div className="um-list">
                                {/* One markup for both layouts: a table-like grid on
                                    desktop, stacked cards on phones (AdminDashboard.css). */}
                                <div className="um-row um-row-head" aria-hidden="true">
                                    <span>User</span><span>ID</span><span>Role</span>
                                    <span>Department</span><span>Branch</span><span>Created</span><span />
                                </div>
                                {filteredUsers.map(u => (
                                    <div key={u._id} className="um-row">
                                        <div className="um-user">
                                            <span className="um-avatar">{initials(u.fullName || u.username)}</span>
                                            <div className="um-user-text">
                                                <div className="um-name">{u.fullName || u.username}</div>
                                                <div className="um-sub">{u.username}</div>
                                            </div>
                                        </div>
                                        <div className="um-id">{u.userId || '—'}</div>
                                        {/* display: contents on desktop (three columns),
                                            one wrapping row of chips on a card. */}
                                        <div className="um-chips">
                                            <div>
                                                <span className={`um-chip um-role ${getRoleBadgeClass(u.roleInfo || u.role)}`}>
                                                    {u.roleInfo?.displayName || u.role || 'No role'}
                                                </span>
                                            </div>
                                            <div>
                                                {u.department
                                                    ? <span className="um-chip">{u.department.name}</span>
                                                    : <span className="um-none">No department</span>}
                                            </div>
                                            <div>
                                                {u.branch
                                                    ? <span className="um-chip">{u.company?.name ? `${u.company.name} — ` : ''}{u.branch.name}</span>
                                                    : <span className="um-none">No branch</span>}
                                            </div>
                                        </div>
                                        <div className="um-created">
                                            {new Date(u.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </div>
                                        <div className="um-actions">
                                            <button className="um-icon-btn" onClick={() => openEditModal(u)} title="Edit user" aria-label="Edit user">
                                                <HiOutlinePencil />
                                            </button>
                                            <button className="um-icon-btn" onClick={() => openPermissionModal(u)} title="Edit permissions" aria-label="Edit permissions">
                                                <HiOutlineKey />
                                            </button>
                                            <button className="um-icon-btn" onClick={() => openPasswordModal(u)} title="Reset password" aria-label="Reset password">
                                                <HiOutlineLockClosed />
                                            </button>
                                            <button className="um-icon-btn danger" onClick={() => handleDeleteUser(u._id)} title="Delete user" aria-label="Delete user">
                                                <HiOutlineTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}
            </div>

            {/* Create User Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New User</h2>
                        <form onSubmit={handleCreateUser}>
                            <div className="form-group">
                                <label className="form-label">Username</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.username}
                                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                                    required
                                    minLength={3}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Password</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    required
                                    minLength={6}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Full Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.fullName}
                                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Role</label>
                                <select
                                    className="form-select"
                                    value={formData.role}
                                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                                    required
                                >
                                    <option value="">Select Role</option>
                                    {roles.map(role => (
                                        <option key={role._id} value={role.roleId}>
                                            {role.displayName || role.roleName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Department *</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select
                                        className="form-select"
                                        value={formData.department}
                                        onChange={(e) => setFormData({...formData, department: e.target.value})}
                                        required
                                        style={{ flex: 1 }}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map(dept => (
                                            <option key={dept._id} value={dept._id}>{dept.name}</option>
                                        ))}
                                    </select>
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary"
                                        onClick={() => setShowCreateDeptModal(true)}
                                        title="Add New Department"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Company *</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select
                                        className="form-select"
                                        style={{ flex: 1 }}
                                        value={formData.company}
                                        onChange={(e) => setFormData({...formData, company: e.target.value, branch: ''})}
                                        required
                                    >
                                        <option value="">Select Company</option>
                                        {companies.map(c => (
                                            <option key={c._id} value={c._id}>{c.name}</option>
                                        ))}
                                    </select>
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateCompanyModal(true)} title="Add New Company">+</button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Branch *</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select
                                        className="form-select"
                                        style={{ flex: 1 }}
                                        value={formData.branch}
                                        onChange={(e) => setFormData({...formData, branch: e.target.value})}
                                        required
                                        disabled={!formData.company}
                                    >
                                        <option value="">{formData.company ? 'Select Branch' : 'Select a company first'}</option>
                                        {branches.map(b => (
                                            <option key={b._id} value={b._id}>{b.name}</option>
                                        ))}
                                    </select>
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateBranchModal(true)} disabled={!formData.company} title="Add New Branch">+</button>
                                </div>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Create User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {showEditModal && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Edit User</h2>
                        <form onSubmit={handleUpdateUser}>
                            <div className="form-group">
                                <label className="form-label">Username</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.username}
                                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                                    required
                                    minLength={3}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Full Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.fullName}
                                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Role</label>
                                <select
                                    className="form-select"
                                    value={formData.role}
                                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                                    required
                                >
                                    <option value="">Select Role</option>
                                    {roles.map(role => (
                                        <option key={role._id} value={role.roleId}>
                                            {role.displayName || role.roleName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Department</label>
                                <select
                                    className="form-select"
                                    value={formData.department}
                                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                                >
                                    <option value="">No Department</option>
                                    {departments.map(dept => (
                                        <option key={dept._id} value={dept._id}>{dept.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Company</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select
                                        className="form-select"
                                        style={{ flex: 1 }}
                                        value={formData.company}
                                        onChange={(e) => setFormData({...formData, company: e.target.value, branch: ''})}
                                    >
                                        <option value="">Select Company</option>
                                        {companies.map(c => (
                                            <option key={c._id} value={c._id}>{c.name}</option>
                                        ))}
                                    </select>
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateCompanyModal(true)} title="Add New Company">+</button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Branch</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select
                                        className="form-select"
                                        style={{ flex: 1 }}
                                        value={formData.branch}
                                        onChange={(e) => setFormData({...formData, branch: e.target.value})}
                                        disabled={!formData.company}
                                    >
                                        <option value="">{formData.company ? 'Select Branch' : 'Select a company first'}</option>
                                        {branches.map(b => (
                                            <option key={b._id} value={b._id}>{b.name}</option>
                                        ))}
                                    </select>
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateBranchModal(true)} disabled={!formData.company} title="Add New Branch">+</button>
                                </div>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Update User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset Password Modal */}
            {/* Reset Password Modal */}
            {showPasswordModal && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Reset Password</h2>
                        <p>Reset password for: <strong>{selectedUser?.username}</strong></p>
                        <form onSubmit={handleResetPassword}>
                            <div className="form-group">
                                <label className="form-label">New Password</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    placeholder="Enter new password (min 6 characters)"
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Reset Password
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Department Modal */}
            {showCreateDeptModal && (
                <div className="modal-overlay" onClick={() => setShowCreateDeptModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Department</h2>
                        <form onSubmit={handleCreateDepartment}>
                            <div className="form-group">
                                <label className="form-label">Department Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newDepartment.name}
                                    onChange={(e) => setNewDepartment({...newDepartment, name: e.target.value})}
                                    required
                                    placeholder="e.g., IT, HR, Finance"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Description</label>
                                <textarea
                                    className="form-input"
                                    value={newDepartment.description}
                                    onChange={(e) => setNewDepartment({...newDepartment, description: e.target.value})}
                                    placeholder="Optional description"
                                    rows="3"
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateDeptModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Create Department
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Manage Departments (heads) Modal */}
            {showManageDeptModal && (
                <div className="modal-overlay" onClick={() => setShowManageDeptModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
                        <h2>Manage Departments</h2>
                        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', marginTop: 0 }}>
                            A department head sees every task belonging to their department in Task
                            Management — view and comment only; they cannot edit, reassign or delete
                            other people's tasks. Heads need not be members of the department they
                            head, so an acting head can cover during leave. This is separate from the
                            <strong> Approve </strong> permission, which only governs approving tasks
                            assigned to that person.
                        </p>

                        {departments.length === 0 ? (
                            <div className="empty-state">No departments yet.</div>
                        ) : (
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>Department</th>
                                        <th>Heads</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {departments.map(dept => (
                                        <tr key={dept._id}>
                                            <td>
                                                {dept.name}
                                                {dept.isActive === false && (
                                                    <span className="no-department"> (inactive)</span>
                                                )}
                                            </td>
                                            <td>
                                                <select
                                                    multiple
                                                    className="form-input"
                                                    size={Math.min(6, Math.max(3, allUsers.length))}
                                                    value={deptHeadsDraft[dept._id] || []}
                                                    onChange={(e) => setDeptHeadsDraft({
                                                        ...deptHeadsDraft,
                                                        [dept._id]: Array.from(e.target.selectedOptions, o => o.value)
                                                    })}
                                                >
                                                    {allUsers.map(u => (
                                                        <option key={u._id} value={u._id}>
                                                            {u.fullName} ({u.username})
                                                        </option>
                                                    ))}
                                                </select>
                                                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                                                    Ctrl/Cmd-click to select more than one, or to clear.
                                                </div>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    disabled={savingDeptId === dept._id}
                                                    onClick={() => handleSaveDeptHeads(dept._id)}
                                                >
                                                    {savingDeptId === dept._id ? 'Saving…' : 'Save'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div className="modal-actions">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowManageDeptModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Company Modal */}
            {showCreateCompanyModal && (
                <div className="modal-overlay" onClick={() => setShowCreateCompanyModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Company</h2>
                        <form onSubmit={handleCreateCompany}>
                            <div className="form-group">
                                <label className="form-label">Company Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newCompany.name}
                                    onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                                    required
                                    placeholder="e.g., Ahlamart"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Code</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newCompany.code}
                                    onChange={(e) => setNewCompany({...newCompany, code: e.target.value})}
                                    placeholder="Optional short code, e.g. AHM"
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateCompanyModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Create Company
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Branch Modal */}
            {showCreateBranchModal && (
                <div className="modal-overlay" onClick={() => setShowCreateBranchModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Branch</h2>
                        <p style={{ marginTop: '-8px', color: '#64748b' }}>
                            Under company: <strong>{companies.find(c => c._id === formData.company)?.name || '—'}</strong>
                        </p>
                        <form onSubmit={handleCreateBranch}>
                            <div className="form-group">
                                <label className="form-label">Branch Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newBranch.name}
                                    onChange={(e) => setNewBranch({...newBranch, name: e.target.value})}
                                    required
                                    placeholder="e.g., Ahlamart - Doha"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Branch Type</label>
                                <select
                                    className="form-select"
                                    value={newBranch.branchType}
                                    onChange={(e) => setNewBranch({...newBranch, branchType: e.target.value})}
                                >
                                    <option value="sales">Sales</option>
                                    <option value="head-office">Head Office</option>
                                    <option value="office">Office</option>
                                    <option value="warehouse">Warehouse</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Code</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newBranch.code}
                                    onChange={(e) => setNewBranch({...newBranch, code: e.target.value})}
                                    placeholder="Optional short code"
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateBranchModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Create Branch
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Permission Editor Modal */}
            {showPermissionModal && selectedUser && (
                <PermissionEditor
                    user={selectedUser}
                    onClose={() => {
                        setShowPermissionModal(false);
                        setSelectedUser(null);
                    }}
                    onUpdate={fetchUsers}
                />
            )}
        </div>
    );
};

export default AdminDashboard;
